import { useState, useEffect } from 'react'
import { portalApiUrl } from '../lib/portalApi'
import { getAuth } from 'firebase/auth'

interface Feature {
  iconName: string
  title: Record<string, string>
  subtitle: Record<string, string>
  iconColorHex: string
  bgColorHex: string
}

interface Offer {
  id: string
  title: Record<string, string>
  subtitle: Record<string, string>
  appIconUrl: string
  productIds: string[]
  features: Feature[]
}

const AVAILABLE_ICONS = [
  { id: 'block', label: 'Block (Cấm/Không có)' },
  { id: 'cloud', label: 'Cloud (Đồng bộ)' },
  { id: 'ai', label: 'AI (Trí tuệ nhân tạo)' },
  { id: 'translate', label: 'Translate (Ngôn ngữ)' },
  { id: 'star', label: 'Star (Nổi bật)' },
  { id: 'check', label: 'Check (Hoàn tất)' }
]

export function PaywallSettingsView({ appId, tenantId }: { appId: string, tenantId: string }) {
  const [offers, setOffers] = useState<Offer[]>([])
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)
  const [lang, setLang] = useState<string>('en')

  const loadPaywall = async () => {
    try {
      setBusy(true)
      const token = await getAuth().currentUser?.getIdToken()
      const res = await fetch(portalApiUrl(`/portal/admin/v1/tenants/${tenantId}/apps/${appId}/settings/paywall`), {
        headers: { 'Authorization': `Bearer ${token}` }
      })
      if (!res.ok) throw new Error('Failed to load Paywall configuration')
      const data = await res.json()
      
      if (data.offers && data.offers.length > 0) {
        const upgradedOffers = data.offers.map((o: any) => ({
          ...o,
          title: typeof o.title === 'string' ? { en: o.title } : (o.title || { en: '' }),
          subtitle: typeof o.subtitle === 'string' ? { en: o.subtitle } : (o.subtitle || { en: '' }),
          features: (o.features || []).map((f: any) => ({
            ...f,
            title: typeof f.title === 'string' ? { en: f.title } : (f.title || { en: '' }),
            subtitle: typeof f.subtitle === 'string' ? { en: f.subtitle } : (f.subtitle || { en: '' }),
          }))
        }))
        setOffers(upgradedOffers)
      } else {
        setOffers([{
          id: 'default',
          title: { en: 'Ebook Libre Pro', vi: 'Gói VIP Pro' },
          subtitle: { en: 'Unlock the future of digital reading.', vi: 'Mở khoá tương lai của đọc sách kỹ thuật số.' },
          appIconUrl: '',
          productIds: ['pro_monthly', 'pro_annual'],
          features: [
            { iconName: 'block', title: { en: 'Ad-free experience', vi: 'Trải nghiệm không quảng cáo' }, subtitle: { en: 'Read your favorite masterpieces without visual interruptions.', vi: 'Đọc các kiệt tác yêu thích của bạn mà không bị gián đoạn.' }, iconColorHex: '#2563EB', bgColorHex: '#DBEAFE' },
            { iconName: 'cloud', title: { en: 'Unlimited Cloud Sync', vi: 'Đồng bộ đám mây không giới hạn' }, subtitle: { en: 'Find your progress and notes on all your devices.', vi: 'Lưu trữ tiến trình và ghi chú trên mọi thiết bị.' }, iconColorHex: '#2563EB', bgColorHex: '#DBEAFE' },
            { iconName: 'ai', title: { en: 'Unlimited AI Tools', vi: 'Công cụ AI không giới hạn' }, subtitle: { en: 'Real-time translation and smart summaries powered by the latest models.', vi: 'Dịch thuật thời gian thực và tóm tắt thông minh bởi các mô hình mới nhất.' }, iconColorHex: '#2563EB', bgColorHex: '#DBEAFE' },
            { iconName: 'translate', title: { en: 'Advanced multilingual support', vi: 'Hỗ trợ đa ngôn ngữ nâng cao' }, subtitle: { en: 'Access contextual dictionaries in over 40 languages.', vi: 'Truy cập từ điển theo ngữ cảnh ở hơn 40 ngôn ngữ.' }, iconColorHex: '#2563EB', bgColorHex: '#DBEAFE' }
          ]
        }])
      }
    } catch (e: any) {
      setError(e.message)
    } finally {
      setBusy(false)
    }
  }

  useEffect(() => {
    loadPaywall()
  }, [appId, tenantId])

  const savePaywall = async () => {
    try {
      setBusy(true)
      setError(null)
      setSuccess(null)
      const token = await getAuth().currentUser?.getIdToken()
      const res = await fetch(portalApiUrl(`/portal/admin/v1/tenants/${tenantId}/apps/${appId}/settings/paywall`), {
        method: 'PUT',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ offers })
      })
      if (!res.ok) throw new Error('Failed to save Paywall configuration')
      setSuccess('Paywall configuration saved successfully!')
      setTimeout(() => setSuccess(null), 3000)
    } catch (e: any) {
      setError(e.message)
    } finally {
      setBusy(false)
    }
  }

  const updateOffer = (index: number, field: keyof Offer, value: any) => {
    const newOffers = [...offers]
    newOffers[index] = { ...newOffers[index], [field]: value }
    setOffers(newOffers)
  }

  const updateOfferI18n = (index: number, field: keyof Offer, value: string) => {
    const newOffers = [...offers]
    const currentMap = newOffers[index][field] as Record<string, string>
    newOffers[index] = { ...newOffers[index], [field]: { ...currentMap, [lang]: value } }
    setOffers(newOffers)
  }

  const addFeature = (offerIndex: number) => {
    const newOffers = [...offers]
    newOffers[offerIndex].features.push({
      iconName: 'check',
      title: { en: '', vi: '' },
      subtitle: { en: '', vi: '' },
      iconColorHex: '#2563EB',
      bgColorHex: '#DBEAFE'
    })
    setOffers(newOffers)
  }

  const updateFeatureI18n = (offerIndex: number, featureIndex: number, field: keyof Feature, value: string) => {
    const newOffers = [...offers]
    const currentMap = newOffers[offerIndex].features[featureIndex][field] as Record<string, string>
    newOffers[offerIndex].features[featureIndex] = { 
      ...newOffers[offerIndex].features[featureIndex], 
      [field]: { ...currentMap, [lang]: value } 
    }
    setOffers(newOffers)
  }

  const updateFeature = (offerIndex: number, featureIndex: number, field: keyof Feature, value: string) => {
    const newOffers = [...offers]
    newOffers[offerIndex].features[featureIndex] = { ...newOffers[offerIndex].features[featureIndex], [field]: value }
    setOffers(newOffers)
  }

  const removeFeature = (offerIndex: number, featureIndex: number) => {
    const newOffers = [...offers]
    newOffers[offerIndex].features.splice(featureIndex, 1)
    setOffers(newOffers)
  }

  return (
    <div className="bg-surface-container-lowest border border-outline-variant rounded-xl p-lg max-w-5xl shadow-sm">
      <div className="flex justify-between items-center mb-lg">
        <h2 className="font-headline-md text-[20px] font-semibold text-on-surface">Paywall Designer</h2>
        <button 
          onClick={savePaywall} 
          disabled={busy}
          className="bg-primary text-on-primary font-label-md px-lg py-sm rounded-lg hover:opacity-90 transition-all shadow-sm disabled:opacity-50"
        >
          {busy ? 'Saving...' : 'Save Configuration'}
        </button>
      </div>
      
      {error && <div className="p-md bg-error-container text-on-error-container border border-error/20 rounded-lg font-body-sm mb-md shadow-sm">{error}</div>}
      {success && <div className="p-md bg-[#15803d]/10 border border-[#15803d]/20 text-[#15803d] rounded-lg font-body-sm mb-md shadow-sm">{success}</div>}

      <div className="flex gap-sm mb-lg">
        {['en', 'vi', 'fr'].map(l => (
          <button 
            key={l}
            onClick={() => setLang(l)} 
            className={`px-md py-sm rounded-md font-label-sm text-[13px] border transition-colors ${lang === l ? 'bg-primary-container text-on-primary-container border-primary-container font-bold' : 'bg-surface-container border-outline-variant text-on-surface hover:bg-surface-container-high'}`}
          >
            {l === 'en' ? 'English (EN)' : l === 'vi' ? 'Tiếng Việt (VI)' : 'Français (FR)'}
          </button>
        ))}
      </div>

      {offers.map((offer, oIndex) => (
        <div key={offer.id} className="mb-lg p-lg bg-surface-container-low border border-outline-variant rounded-xl shadow-sm">
          <h3 className="font-headline-sm text-[16px] font-semibold text-on-surface mb-md">Offer ID: {offer.id}</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-md mb-md">
            <div>
              <label className="block font-label-sm text-[12px] text-on-surface-variant mb-1">Title [{lang.toUpperCase()}]</label>
              <input 
                className="w-full bg-surface-container-lowest border border-outline-variant rounded-lg px-3 py-2 font-body-sm focus:outline-none focus:ring-2 focus:ring-primary/50 text-on-surface" 
                value={offer.title[lang] || ''} 
                onChange={(e) => updateOfferI18n(oIndex, 'title', e.target.value)} 
              />
            </div>
            <div>
              <label className="block font-label-sm text-[12px] text-on-surface-variant mb-1">Subtitle [{lang.toUpperCase()}]</label>
              <input 
                className="w-full bg-surface-container-lowest border border-outline-variant rounded-lg px-3 py-2 font-body-sm focus:outline-none focus:ring-2 focus:ring-primary/50 text-on-surface" 
                value={offer.subtitle[lang] || ''} 
                onChange={(e) => updateOfferI18n(oIndex, 'subtitle', e.target.value)} 
              />
            </div>
          </div>

          <div className="mb-lg">
            <label className="block font-label-sm text-[12px] text-on-surface-variant mb-1">Product IDs (comma separated)</label>
            <input 
              className="w-full bg-surface-container-lowest border border-outline-variant rounded-lg px-3 py-2 font-body-sm focus:outline-none focus:ring-2 focus:ring-primary/50 text-on-surface font-mono" 
              value={offer.productIds.join(', ')} 
              onChange={(e) => updateOffer(oIndex, 'productIds', e.target.value.split(',').map((s: string) => s.trim()).filter(Boolean))} 
            />
          </div>

          <div className="flex justify-between items-center mb-md border-t border-outline-variant pt-md">
            <h4 className="font-headline-sm text-[16px] font-semibold text-on-surface">Features List</h4>
            <button 
              onClick={() => addFeature(oIndex)} 
              className="font-label-sm text-[12px] bg-secondary-container text-on-secondary-container px-3 py-1.5 rounded hover:opacity-90 transition-opacity"
            >
              + Add Feature
            </button>
          </div>

          <div className="space-y-sm">
            {offer.features.map((feature, fIndex) => (
              <div key={fIndex} className="grid grid-cols-12 gap-sm items-end bg-surface-container-lowest p-md rounded-lg border border-outline-variant/50 shadow-sm relative group">
                <div className="col-span-12 md:col-span-2">
                  <label className="block font-label-sm text-[11px] text-on-surface-variant mb-1">Icon</label>
                  <select 
                    value={feature.iconName}
                    onChange={(e) => updateFeature(oIndex, fIndex, 'iconName', e.target.value)}
                    className="w-full bg-surface-container border border-outline-variant text-on-surface px-2 py-1.5 rounded text-[13px] focus:outline-none focus:ring-1 focus:ring-primary/50"
                  >
                    {AVAILABLE_ICONS.map(ic => <option key={ic.id} value={ic.id}>{ic.label}</option>)}
                  </select>
                </div>
                <div className="col-span-12 md:col-span-3">
                  <label className="block font-label-sm text-[11px] text-on-surface-variant mb-1">Title [{lang}]</label>
                  <input 
                    className="w-full bg-surface-container border border-outline-variant text-on-surface px-2 py-1.5 rounded text-[13px] focus:outline-none focus:ring-1 focus:ring-primary/50" 
                    value={feature.title[lang] || ''} 
                    onChange={(e) => updateFeatureI18n(oIndex, fIndex, 'title', e.target.value)} 
                  />
                </div>
                <div className="col-span-12 md:col-span-3">
                  <label className="block font-label-sm text-[11px] text-on-surface-variant mb-1">Subtitle [{lang}]</label>
                  <input 
                    className="w-full bg-surface-container border border-outline-variant text-on-surface px-2 py-1.5 rounded text-[13px] focus:outline-none focus:ring-1 focus:ring-primary/50" 
                    value={feature.subtitle[lang] || ''} 
                    onChange={(e) => updateFeatureI18n(oIndex, fIndex, 'subtitle', e.target.value)} 
                  />
                </div>
                <div className="col-span-6 md:col-span-2">
                  <label className="block font-label-sm text-[11px] text-on-surface-variant mb-1">Icon Hex</label>
                  <input 
                    className="w-full bg-surface-container border border-outline-variant text-on-surface px-2 py-1.5 rounded text-[13px] font-mono focus:outline-none focus:ring-1 focus:ring-primary/50" 
                    value={feature.iconColorHex} 
                    onChange={(e) => updateFeature(oIndex, fIndex, 'iconColorHex', e.target.value)} 
                  />
                </div>
                <div className="col-span-6 md:col-span-2">
                  <label className="block font-label-sm text-[11px] text-on-surface-variant mb-1">Bg Hex</label>
                  <input 
                    className="w-full bg-surface-container border border-outline-variant text-on-surface px-2 py-1.5 rounded text-[13px] font-mono focus:outline-none focus:ring-1 focus:ring-primary/50" 
                    value={feature.bgColorHex} 
                    onChange={(e) => updateFeature(oIndex, fIndex, 'bgColorHex', e.target.value)} 
                  />
                </div>
                <button 
                  onClick={() => removeFeature(oIndex, fIndex)} 
                  className="absolute top-2 right-2 text-error hover:bg-error-container hover:text-on-error-container w-6 h-6 rounded-full flex items-center justify-center transition-colors opacity-0 group-hover:opacity-100"
                  title="Remove Feature"
                >
                  <span className="material-symbols-outlined text-[16px]">close</span>
                </button>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  )
}
